import * as pdfjsLib from 'pdfjs-dist';

// Set worker source for browser
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs`;

interface TextItem {
  str: string;
  dir: string;
  width: number;
  height: number;
  transform: number[];
  fontName: string;
  hasEOL: boolean;
}

/**
 * Extract text from PDF with improved layout preservation.
 * Sorts text items by vertical position first, then horizontal,
 * to better handle multi-column layouts (common in skills sections).
 */
export async function extractTextFromPdf(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  
  let fullText = '';
  
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    
    // Extract items with their positions
    const items = textContent.items
      .filter((item: any): item is TextItem => item && typeof item.str === 'string')
      .map((item: TextItem) => {
        // transform matrix: [scaleX, skewY, skewX, scaleY, tx, ty]
        // tx, ty are the position coordinates
        const x = item.transform[4];
        const y = item.transform[5];
        return {
          text: item.str,
          x,
          y,
          width: item.width,
          height: item.height || 12,
          hasEOL: item.hasEOL || false,
        };
      });
    
    // Group items by line (similar y-coordinate)
    const lineThreshold = 3; // pixels
    const lines: { y: number; items: typeof items }[] = [];
    
    for (const item of items) {
      // Find existing line with similar y-coordinate
      const line = lines.find(l => Math.abs(l.y - item.y) <= lineThreshold);
      if (line) {
        line.items.push(item);
        // Update line y to average
        line.y = (line.y * (line.items.length - 1) + item.y) / line.items.length;
      } else {
        lines.push({ y: item.y, items: [item] });
      }
    }
    
    // Sort lines top-to-bottom (PDF y-coordinates: 0 is bottom, so sort descending)
    lines.sort((a, b) => b.y - a.y);
    
    // For each line, sort items left-to-right
    let pageText = '';
    for (const line of lines) {
      line.items.sort((a, b) => a.x - b.x);
      
      // Detect multi-column layout and add spacing
      const lineText = line.items.map((item, idx) => {
        const prevItem = line.items[idx - 1];
        let prefix = '';
        
        if (prevItem) {
          const gap = item.x - (prevItem.x + prevItem.width);
          // Resume tag grids often have narrow gutters. Preserve those item
          // boundaries so the parser does not merge a full row into one skill.
          const columnGap = Math.max(6, Math.min(prevItem.height, item.height) * 0.5);
          if (gap > columnGap) {
            prefix = ' | ';
          } else if (gap > 1) {
            prefix = ' ';
          }
        }
        
        return prefix + item.text;
      }).join('');
      
      pageText += lineText + '\n';
    }
    
    // Clean up the text
    const cleanedPageText = pageText
      .replace(/  +/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    fullText += cleanedPageText + '\n\n';
  }
  
  return fullText.trim();
}
